import { getStorage, ref, uploadBytes } from "firebase/storage";
import { firebaseApp } from "../../../api/firebase/firestoreService";
import { logDataToConsole, logError } from "../../../utils/customLogger";

/**
 * Metadata describing a support ticket attachment uploaded to Firebase Storage.
 */
export interface SupportTicketAttachmentUpload {
  /** Storage path the file was uploaded to (sent to the backend as gcs_path). */
  gcs_path: string;
  /** Original file name. */
  filename: string;
  /** MIME type of the uploaded file. */
  content_type: string;
  /** File size in bytes. */
  size: number;
}

/**
 * Builds the tenant-scoped storage path for a ticket attachment.
 *
 * Must match the prefix the backend enforces:
 * `support_tickets/attachments/{orgId}/{ticketId}/`.
 *
 * @param orgId - Organization/facility id.
 * @param ticketId - Parent ticket id.
 * @param fileName - Original file name.
 * @returns Storage path for the attachment.
 */
function buildAttachmentPath(
  orgId: string,
  ticketId: string,
  fileName: string,
): string {
  const uniquePrefix =
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `${Date.now()}`;
  return `support_tickets/attachments/${orgId}/${ticketId}/${uniquePrefix}_${fileName}`;
}

/**
 * Uploads a support ticket attachment to Firebase Storage.
 *
 * @param file - File selected in the attachment modal.
 * @param orgId - Organization/facility id.
 * @param ticketId - Parent ticket id.
 * @returns Storage path and metadata to register with the backend.
 */
export const uploadSupportTicketAttachmentToStorage = async (
  file: File,
  orgId: string,
  ticketId: string,
): Promise<SupportTicketAttachmentUpload> => {
  try {
    const storage = getStorage(firebaseApp);
    const path = buildAttachmentPath(orgId, ticketId, file.name);
    const snapshot = await uploadBytes(ref(storage, path), file);

    const result: SupportTicketAttachmentUpload = {
      gcs_path: snapshot.ref.fullPath,
      filename: file.name,
      content_type: file.type || "application/octet-stream",
      size: file.size,
    };
    logDataToConsole("uploadSupportTicketAttachmentToStorage", result);
    return result;
  } catch (error) {
    logError(
      error as Error,
      "Error uploading support ticket attachment to storage",
    );
    throw error;
  }
};
